import type { Employee, EmploymentType } from '../../data/types';
import type { EmployeesState } from '../../data/use_employees';

export type SortDirection = 'asc' | 'desc';

/**
 * What the directory screen should render for a given `EmployeesState` +
 * filtered row count. Extracted as a pure, independently-tested function
 * (mirrors `resolveAuthGateScreen`, `lib/auth/auth_gate_screen.ts` — same
 * "ordering regression -> one unit test away from being caught" rationale)
 * so the render-boundary ordering bug PR #22369's adversarial review found
 * (the directory screen used to destructure only `{ employees }` from
 * `useEmployees()`, so a genuine `loading` fetch, a missing-`payroll`-
 * entitlement `entitlement_required`, and a real `error` all rendered the
 * exact same "No employees match your search" empty-state copy) cannot
 * silently regress: `loading`/`no_entity`/`entitlement_required`/`error`
 * MUST all be checked before the `ready`-with-zero-filtered-rows branch,
 * never the other way around.
 *
 * `no_entity` is included for completeness/defensiveness even though
 * `AuthGate` (`main.tsx`) already gates a `no_entity` `useSessionFacts()`
 * result before the app shell (and therefore this screen) ever mounts — see
 * `use_employees.ts`'s `EmployeesState` docblock.
 */
export type EmployeeDirectoryView =
  | 'loading'
  | 'no_entity'
  | 'entitlement_required'
  | 'error'
  | 'no_employees'
  | 'empty'
  | 'rows';

export function resolve_employee_directory_view(input: {
  state: EmployeesState;
  filtered_row_count: number;
}): EmployeeDirectoryView {
  if (input.state.status === 'loading') return 'loading';
  if (input.state.status === 'no_entity') return 'no_entity';
  if (input.state.status === 'entitlement_required') {
    return 'entitlement_required';
  }
  if (input.state.status === 'error') return 'error';
  // input.state.status === 'ready'
  // A company with nobody on its books yet is a first-run state, not a search
  // miss -- "No employees match your search" there reads as if a filter the
  // user never typed is hiding someone.
  if (input.state.employees.length === 0) return 'no_employees';
  return input.filtered_row_count === 0 ? 'empty' : 'rows';
}

/**
 * Directory search — matches name, department or job title, case-insensitively.
 * A blank or whitespace-only query matches everything rather than nothing.
 */
export function filter_employees(
  employees: readonly Employee[],
  query: string,
): Employee[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return [...employees];

  return employees.filter((employee) =>
    [employee.name, employee.department, employee.job_title].some((field) =>
      field.toLowerCase().includes(needle),
    ),
  );
}

/**
 * Sort by name using locale collation, so Sinhala and Tamil names order
 * correctly rather than by UTF-16 code unit.
 */
export function sort_employees(
  employees: readonly Employee[],
  direction: SortDirection,
  locale: string,
): Employee[] {
  const collator = new Intl.Collator(locale, { sensitivity: 'base' });
  return [...employees].sort((a, b) =>
    direction === 'asc'
      ? collator.compare(a.name, b.name)
      : collator.compare(b.name, a.name),
  );
}

export function toggle_direction(direction: SortDirection): SortDirection {
  return direction === 'asc' ? 'desc' : 'asc';
}

/** The directory's worker-type filter: every type, or one of them. */
export type EmployeeTypeFilter = 'all' | EmploymentType;

/** Keep only the workers of the selected type (`'all'` keeps everyone). */
export function filter_employees_by_type(
  employees: readonly Employee[],
  type: EmployeeTypeFilter,
): Employee[] {
  if (type === 'all') return [...employees];
  return employees.filter((employee) => employee.employment_type === type);
}

/**
 * Per-type head counts for the filter tabs, plus `all`. Every type is
 * present (0 when nobody has it) so a tab never renders without a count.
 */
export function count_employees_by_type(
  employees: readonly Employee[],
): Record<EmployeeTypeFilter, number> {
  const counts: Record<EmployeeTypeFilter, number> = {
    all: employees.length,
    full_time: 0,
    part_time: 0,
    contract: 0,
    contractor: 0,
  };
  for (const employee of employees) {
    counts[employee.employment_type] += 1;
  }
  return counts;
}

const TYPE_FILTER_IDS: readonly EmployeeTypeFilter[] = [
  'all',
  'full_time',
  'part_time',
  'contract',
  'contractor',
];

/** Narrow a tab id back to a filter; anything unknown falls back to `'all'`. */
export function parse_employee_type_filter(id: string): EmployeeTypeFilter {
  return TYPE_FILTER_IDS.find((candidate) => candidate === id) ?? 'all';
}
